// ----------------------------------------------------------------------- imports externos
import { useEffect, useRef, useState } from "react";

// ----------------------------------------------------------------------- imports internos
import { estadoInstalacion, alCambiarInstalacion, instalar } from "../../pwa";
import {
  ACENTOS,
  aplicarPrefs,
  guardarPrefsLocal,
  leerPrefs,
  parDeAcento,
  PREF_DEF,
} from "../../functions/preferencias";
import {
  borrarFondo,
  guardarPrefs,
  subirFondo,
  traerPrefs,
} from "../../services/preferencias";
import { temasVisibles } from "../../shared/registroTemas";
import SelectorDeTemas from "./SelectorDeTemas";
import LlavesDeAcceso from "../acceso/LlavesDeAcceso";

/* Panel de preferencias: tema, acento, fondo y transparencia.
 *
 * Se guardan por persona y EN LA BASE, así que siguen a quien las eligió: el
 * mismo tema en el celular, en la computadora de la oficina y en la de casa.
 * Antes vivían en el navegador, y eso significaba una configuración distinta
 * por máquina sin que nadie la hubiera pedido.
 *
 * Se aplica al toque y se guarda en segundo plano. Un botón de "Guardar" acá
 * sería el peor de los dos mundos: hay que verlo para saber si quedó bien, y
 * la mitad de las veces alguien cierra el panel sin tocarlo.
 */

// La lista de themes sale del registro único (`shared/registroTemas`). Sumar un
// theme nuevo aparece acá solo con agregarlo allá.

// El tope del bucket. Se corta antes de subir para poder explicar el motivo en
// vez de que falle a mitad de camino.
const TOPE_FONDO = 3 * 1024 * 1024;

// Los deslizadores disparan un cambio por pixel. Se aplica en pantalla al
// instante, pero al servidor se le manda una sola vez cuando la mano se
// detiene: mover el zoom de punta a punta son doscientas escrituras.
const ESPERA = 600;

const Preferencias = ({ abierto, onCerrar, idPersona, nombrePersona }) => {
  const [prefs, setPrefs] = useState(() => leerPrefs(idPersona));
  const [aviso, setAviso] = useState(null);
  const archivoRef = useRef(null);

  // Estado de la instalación. Vive acá porque el botón vive acá: en la
  // computadora instalar es algo de una sola vez, así que no merece lugar
  // permanente en la cabecera.
  const [pwa, setPwa] = useState(estadoInstalacion);
  useEffect(() => alCambiarInstalacion(setPwa), []);

  const instalarAhora = async () => {
    const r = await instalar();
    if (r === "dismissed") {
      setAviso({
        texto:
          "No pasa nada: podés volver a intentarlo desde acá cuando quieras.",
        tipo: "ok",
      });
    }
  };

  // Al cambiar de persona se cargan las suyas: primero la copia local, que es
  // instantánea, y después lo que diga la base, que es lo que manda.
  useEffect(() => {
    const p = leerPrefs(idPersona);
    setPrefs(p);
    aplicarPrefs(p);
    setAviso(null);

    let vigente = true;
    traerPrefs().then((remotas) => {
      if (!vigente || !remotas) return;
      const nuevas = { ...PREF_DEF, ...remotas };
      setPrefs(nuevas);
      aplicarPrefs(nuevas);
      guardarPrefsLocal(idPersona, nuevas);
    });
    return () => {
      vigente = false;
    };
  }, [idPersona]);

  // Con tema automático hay que seguir al sistema en vivo: si alguien cambia
  // el modo oscuro del celular con la app abierta, el acento tiene que
  // recalcularse (usa una variante distinta por tema).
  useEffect(() => {
    if (prefs.tema !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const alCambiar = () => aplicarPrefs(prefs);
    mq.addEventListener("change", alCambiar);
    return () => mq.removeEventListener("change", alCambiar);
  }, [prefs]);

  const pendiente = useRef(null);
  useEffect(() => () => clearTimeout(pendiente.current), []);

  const actualizar = (cambio) => {
    const nuevas = { ...prefs, ...cambio };
    setPrefs(nuevas);
    aplicarPrefs(nuevas);
    guardarPrefsLocal(idPersona, nuevas);

    clearTimeout(pendiente.current);
    pendiente.current = setTimeout(async () => {
      const problema = await guardarPrefs(nuevas);
      // Sólo se avisa si falló. Un cartel de "guardado" en cada click de un
      // deslizador es ruido, y el cambio ya se ve en pantalla.
      if (problema)
        setAviso({
          texto:
            "Se aplicó acá, pero no se pudo guardar en el servidor: " +
            problema,
          tipo: "alerta",
        });
    }, ESPERA);
  };

  const elegirFondo = async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo) return;

    if (archivo.size > TOPE_FONDO) {
      setAviso({
        texto:
          "Esa imagen pesa más de 3 MB. Probá con una más chica, o con una captura en vez de la foto original.",
        tipo: "error",
      });
      return;
    }

    setAviso({ texto: "Subiendo la imagen…", tipo: "ok" });
    const anterior = prefs.fondoRuta;
    try {
      const { ruta, url } = await subirFondo(archivo);
      actualizar({ fondo: url, fondoRuta: ruta });
      // Recién cuando la nueva está arriba. Al revés, un error a mitad de
      // camino deja sin fondo a alguien que sólo quería cambiarlo.
      if (anterior && anterior !== ruta) borrarFondo(anterior);
      setAviso(null);
    } catch (err) {
      setAviso({
        texto: "No se pudo subir la imagen: " + (err.message || "error"),
        tipo: "error",
      });
    }
  };

  const quitarFondo = () => {
    const anterior = prefs.fondoRuta;
    actualizar({ fondo: null, fondoRuta: null });
    if (anterior) borrarFondo(anterior);
    setAviso(null);
  };

  return (
    <aside
      className={"hub-panel" + (abierto ? " abierto" : "")}
      aria-label="Preferencias"
      aria-hidden={!abierto}
    >
      <button className="cerrar" onClick={onCerrar} aria-label="Cerrar">
        ✕
      </button>
      <h2>Preferencias</h2>
      <div className="de-quien">De {nombrePersona || "vos"}</div>

      {/* Los temas del rediseño (UAT). Fuera de UAT no dibuja nada. */}
      <SelectorDeTemas />

      <div className="hub-pref">
        <div className="et">Tema</div>
        <div className="hub-seg">
          {temasVisibles().map(({ id, label }) => (
            <button
              key={id}
              type="button"
              aria-pressed={prefs.tema === id}
              onClick={() => actualizar({ tema: id })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="hub-pref">
        <div className="et">Color de acento</div>
        <div className="hub-swatches">
          {ACENTOS.map(([nombre, claro]) => (
            <button
              key={claro}
              type="button"
              className="hub-sw"
              style={{ background: claro }}
              aria-pressed={prefs.acento === claro}
              aria-label={nombre}
              title={nombre}
              onClick={() => actualizar(parDeAcento(claro))}
            />
          ))}
        </div>
      </div>

      <div className="hub-pref">
        <div className="et">Fondo</div>
        <label className="hub-archivo">
          Elegir una imagen…
          <input
            ref={archivoRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={elegirFondo}
          />
        </label>
        {aviso && (
          <div className={"hub-aviso " + aviso.tipo} role="status">
            {aviso.texto}
          </div>
        )}
        {prefs.fondo && (
          <>
            {/* Con `cover`, una foto recortada al centro suele cortar justo lo
                que uno quería ver. Estos tres controles son para acomodarla. */}
            <div className="hub-deslizador">
              <span>Horizontal</span>
              <input
                type="range"
                min="0"
                max="100"
                value={prefs.fondoX ?? 50}
                onChange={(e) =>
                  actualizar({ fondoX: Number(e.target.value) })
                }
                aria-label="Mover la imagen a los costados"
              />
            </div>
            <div className="hub-deslizador">
              <span>Vertical</span>
              <input
                type="range"
                min="0"
                max="100"
                value={prefs.fondoY ?? 50}
                onChange={(e) =>
                  actualizar({ fondoY: Number(e.target.value) })
                }
                aria-label="Mover la imagen arriba y abajo"
              />
            </div>
            <div className="hub-deslizador">
              <span>Acercar</span>
              <input
                type="range"
                min="100"
                max="260"
                value={prefs.fondoZoom ?? 100}
                onChange={(e) =>
                  actualizar({ fondoZoom: Number(e.target.value) })
                }
                aria-label="Acercar o alejar la imagen"
              />
            </div>
            <button className="hub-quitar" onClick={quitarFondo}>
              Quitar fondo
            </button>
          </>
        )}
      </div>

      <div className="hub-pref">
        <div className="hub-fila">
          <div>
            Transparencia
            <div className="desc">
              Difumina lo que queda detrás del menú
            </div>
          </div>
          <button
            className="hub-switch"
            role="switch"
            aria-checked={!!prefs.blur}
            aria-label="Transparencia"
            onClick={() => actualizar({ blur: !prefs.blur })}
          />
        </div>
      </div>

      {/* Entrar sin clave (Igna, 02/11). */}
      <LlavesDeAcceso abierto={abierto} />

      {/* Instalar vive acá y no en la cabecera. En la computadora es algo que
          se hace una vez y nunca más, así que un botón permanente arriba a la
          derecha ocupa lugar todos los días para algo de un solo uso. */}
      <div className="hub-pref">
        <div className="et">Instalar como app</div>
        {pwa.yaInstalada ? (
          <p className="desc" style={{ fontSize: "12.5px" }}>
            Ya está instalada. La estás usando así.
          </p>
        ) : (
          <>
            <p className="desc" style={{ fontSize: "12.5px", marginBottom: 8 }}>
              Icono propio en el escritorio o en el celular, y se abre sin la
              barra del navegador.
            </p>
            {pwa.sePuedeInstalar ? (
              <button className="hub-instalar" onClick={instalarAhora}>
                Instalar
              </button>
            ) : (
              <p className="desc" style={{ fontSize: "12px", lineHeight: 1.5 }}>
                Este navegador no ofrece instalarla. En <b>iPhone</b>: Compartir
                → Agregar a inicio. En <b>Android o computadora</b>: buscá
                Instalar en el menú del navegador. Con Chrome, Edge o Safari
                funciona.
              </p>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

export default Preferencias;
